import React from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import Helmet from 'react-helmet'
import PropTypes from 'prop-types'
import uuidv4 from 'uuid/v4'

import { storage } from '../utils'

const formatBreadcrumb = crumb => crumb.replace('-', ' ').replace(/(^|\s)\S/g, letter => letter.toUpperCase())

const createBreadcrumb = (name, pathname) =>
  `${name}${pathname
    .split('/')
    .map(formatBreadcrumb)
    .join(' > ')}`

function getFeatures(menu, features = []) {
  menu.map(link => link.name === 'Software' && link.submenu && link.submenu.map(child => features.push(child.name)))

  return features.join(', ')
}

const defaultKeywords = [
  'route',
  'route the service platform',
  'building services software',
  'commercial cleaning software'
]

const Head = (
  {
    article,
    banner,
    backstory,
    body,
    children,
    date,
    description,
    faq,
    howto,
    keywords,
    pathname,
    qa,
    section,
    speakable,
    support,
    timeToRead,
    title,
    video,
    wordCount,
    metaTitle,
    socialTitle,
    socialDescription
  },
  ...props
) => {
  let image
  let imageHeight = 1230
  let imageWidth = 600
  let userId

  if (storage && storage.getItem('userId')) {
    userId = storage.getItem('userId')
  } else {
    userId = uuidv4()

    if (storage) {
      storage.setItem('userId', JSON.stringify(userId))
    }
  }

  const data = useStaticQuery(graphql`
    query Head {
      logo: file(relativePath: { eq: "assets/logo/route-logo-full@2x.png" }) {
        publicURL
      }
      site: site {
        buildTime(formatString: "YYYY-MM-DD")
        siteMetadata {
          shortName
          author
          siteLanguage
          siteUrl
          defaultTitle
          defaultDescription
          defaultBanner
          titleAlt
          social
          themeColor
          url
          menuLinks {
            name
            submenu {
              name
            }
          }
        }
      }
    }
  `)

  const {
    logo,
    site: {
      buildTime,
      siteMetadata: {
        shortName,
        author,
        siteLanguage,
        siteUrl,
        defaultTitle,
        defaultDescription,
        defaultBanner,
        titleAlt,
        social,
        themeColor,
        menuLinks,
        url
      }
    }
  } = data

  if (!pathname) {
    if (props.location && props.location.pathname) {
      pathname = props.location.pathname
    } else if (global.location && global.location.pathname) {
      pathname = global.location.pathname
    } else {
      pathname = '/'
    }
  }

  if (pathname.includes('.html')) {
    pathname = pathname.split('.')[0]
  }

  if (!title) {
    title = defaultTitle
  }

  if (!description) {
    description = defaultDescription
  }

  if (keywords.length > 0) {
    keywords = defaultKeywords
  }

  if (banner && banner.childImageSharp) {
    if (banner.childImageSharp.fixed) {
      image = `${url}${banner.childImageSharp.fixed.src}`

      if (banner.childImageSharp.fixed.height) {
        imageHeight = banner.childImageSharp.fixed.height
      }

      if (banner.childImageSharp.fixed.width) {
        imageWidth = banner.childImageSharp.fixed.width
      }
    }

    if (banner.childImageSharp.fluid) {
      image = `${url}${banner.childImageSharp.fluid.src}`

      if (banner.childImageSharp.fluid.presentationHeight) {
        imageHeight = banner.childImageSharp.fluid.presentationHeight
      }

      if (banner.childImageSharp.fluid.presentationWidth) {
        imageWidth = banner.childImageSharp.fluid.presentationWidth
      }
    }

    if (banner.childImageSharp.resize) {
      image = `${url}${banner.childImageSharp.resize.src}`

      if (banner.childImageSharp.resize.height) {
        imageHeight = banner.childImageSharp.resize.height
      }

      if (banner.childImageSharp.resize.width) {
        imageWidth = banner.childImageSharp.resize.width
      }
    }
  } else if (banner && banner.publicURL) {
    image = `${url}${banner.publicURL}`
  }
  // } else {
  //   image = `${url}${logo.publicURL}`
  // }

  if (date && date !== 'undefined') {
    date = new Date(date)
  } else {
    date = Date.now()
  }

  const breadcrumb = createBreadcrumb(shortName, pathname)
  const seo = {
    title,
    date,
    description: description.replace(/ {2}|\r\n|\n|\r/gm, ''),
    image,
    keywords: keywords.join(`, `),
    timeToRead: `PT${timeToRead * 60}S` || 'PT300S',
    url: `${url}${pathname && pathname.length > 1 ? pathname : ''}`,
    metaTitle: metaTitle && metaTitle.length > 0 ? metaTitle : title,
    socialTitle: socialTitle && socialTitle.length > 0 ? socialTitle : title,
    socialDescription:
      socialDescription && socialDescription.length > 0
        ? socialDescription.replace(/ {2}|\r\n|\n|\r/gm, '')
        : description.replace(/ {2}|\r\n|\n|\r/gm, '')
  }

  const OrganizationJSONLD = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': '#routeOrg',
    url,
    name: shortName,
    logo: {
      '@type': 'ImageObject',
      url: `${url}${logo.publicURL}`
    },
    sameAs: [
      'http://www.facebook.com/routeplatform',
      'http://instagram.com/route_platform',
      'http://www.linkedin.com/in/route_platform',
      'http://twitter.com/route_platform',
      'https://www.youtube.com/channel/UCotpo-oU-62ykPzT4-R_UQQ'
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+1-312-877-9127',
        contactType: 'customer service',
        contactOption: 'TollFree',
        availableLanguage: ['English', 'Spanish'],
        areaServed: 'US'
      }
    ]
  }

  const BrandJSONLD = {
    '@context': 'https://schema.org',
    '@type': 'Brand',
    logo: {
      '@type': 'ImageObject',
      url: `${url}${logo.publicURL}`
    },
    mainEntityOfPage: {
      '@id': '#routePlatform'
    },
    name: shortName,
    alternateName: defaultTitle,
    description:
      'Route is a business management platform for Service Companies. Featuring Solutions for Walkthroughs, Bidding, Contract Management, Scheduling, Inspections, and Workforce Management, our SaaS based Software was built for the unique and complex needs across industries such as Building and Field Services, Facility Maintenance, and Property Management.',
    sameAs: [
      'http://www.facebook.com/routeplatform',
      'http://instagram.com/route_platform',
      'http://www.linkedin.com/in/route_platform',
      'http://twitter.com/route_platform',
      'https://www.youtube.com/channel/UCotpo-oU-62ykPzT4-R_UQQ'
    ],
    slogan: 'The Service Platform',
    url
  }

  const LocalBusinessJSONLD = {
    '@context': 'http://schema.org',
    '@type': 'LocalBusiness',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Chicago',
      addressRegion: 'IL',
      streetAddress: '6120 N Pulaski Rd'
    },
    description:
      'Route is a business management platform for Service Companies. Featuring Solutions for Walkthroughs, Bidding, Contract Management, Scheduling, Inspections, and Workforce Management, our SaaS based Software was built for the unique and complex needs across industries such as Building and Field Services, Facility Maintenance, and Property Management.',
    image: {
      '@type': 'ImageObject',
      url: `${url}${logo.publicURL}`
    },
    name: 'Route',
    priceRange: '$',
    telephone: '+1-312-877-9127',
    url: 'https://getroute.com',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+1-312-877-9127',
        contactType: 'customer service',
        contactOption: 'TollFree',
        availableLanguage: ['English', 'Spanish'],
        areaServed: 'US'
      }
    ]
  }

  const WebsiteJSONLD = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    '@id': url,
    url,
    name: defaultTitle,
    alternateName: titleAlt || ''
  }

  // const UserReviewJSONLD = {
  //   '@context': 'https://schema.org/',
  //   '@type': 'UserReview',
  //   author: {
  //     '@type': 'Person',
  //     name: 'Marc Battista'
  //   },
  //   datePublished: '2011-08-19T13:00:00-0500',
  //   itemReviewed: {
  //     '@id': '#routePlatform'
  //   },
  //   name: 'Google Review By Marc Battista',
  //   reviewRating: {
  //     '@type': 'Rating',
  //     ratingValue: '5',
  //     bestRating: '5',
  //     worstRating: '1'
  //   },
  //   publisher: {
  //     '@id': '#routeOrg'
  //   },
  //   reviewBody:
  //     'The Route Team has Top notch customer service. When we have questions, we get answers. The apps versatility to provide service industry workers with a way to catalogue the requirements of a job, pricing a job and then to produce a proposal makes Route a must have app in today’s fast pace business environment.'
  // }

  // const FreeOfferJSONLD = {
  //   '@type': 'Offer',
  //   name: 'Free',
  //   priceSpecification: {
  //     '@type': 'UnitPriceSpecification',
  //     price: '0',
  //     priceCurrency: 'USD',
  //     referenceQuantity: {
  //       '@type': 'QuantitativeValue',
  //       priceType: 'Monthly',
  //       value: '1',
  //       unitCode: 'MON'
  //     }
  //   },
  //   acceptedPaymentMethod: 'http://purl.org/goodrelations/v1#ByInvoice',
  //   availability: 'OnlineOnly',
  //   businessFunction: 'http://purl.org/goodrelations/v1#ProvideService',
  //   category:
  //     'Software as a Service > Business Management Platform > Service Providers',
  //   itemOffered: {
  //     '@id': '#Free',
  //     '@type': 'Service',
  //     name: 'Free Tier',
  //     description:
  //       'All Sales Features for 5 Users. 10 Walkthroughs, or Inspections per month. Limit 1 Proposal. Free forever.',
  //     sameAs: 'https://en.wikipedia.org/wiki/Software_as_a_service'
  //   },
  //   offeredBy: OrganizationJSONLD,
  //   seller: OrganizationJSONLD,
  //   url: 'https://getroute.com/pricing'
  // }

  const StarterMonthlyOfferJSONLD = {
    '@type': 'Offer',
    name: 'Starter',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '49',
      priceCurrency: 'USD',
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        priceType: 'Monthly',
        value: '1',
        unitCode: 'MON'
      }
    },
    acceptedPaymentMethod: 'http://purl.org/goodrelations/v1#ByInvoice',
    availability: 'OnlineOnly',
    businessFunction: 'http://purl.org/goodrelations/v1#ProvideService',
    category: 'Software as a Service > Business Management Platform > Service Providers',
    itemOffered: {
      '@id': '#StarterMonthly',
      '@type': 'Service',
      name: 'Starter Tier',
      description:
        'All Sales Features for 10 Users. Unlimited Walkthroughs, and Inspections. Limit 5 Proposals. Paid Monthly.',
      sameAs: 'https://en.wikipedia.org/wiki/Software_as_a_service'
    },
    offeredBy: {
      '@id': '#routeOrg'
    },
    seller: {
      '@id': '#routeOrg'
    },
    url: 'https://getroute.com/pricing'
  }

  const GrowthMonthlyOfferJSONLD = {
    '@type': 'Offer',
    name: 'Growth',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '129',
      priceCurrency: 'USD',
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        priceType: 'Monthly',
        value: '1',
        unitCode: 'MON'
      }
    },
    acceptedPaymentMethod: 'http://purl.org/goodrelations/v1#ByInvoice',
    availability: 'OnlineOnly',
    businessFunction: 'http://purl.org/goodrelations/v1#ProvideService',
    category: 'Software as a Service > Business Management Platform > Service Providers',
    itemOffered: {
      '@id': '#GrowthMonthly',
      '@type': 'Service',
      name: 'Growth Tier',
      description: 'All Sales Features for 10 Users. Unlimited Walkthroughs, Proposals, and Inspections. Paid Monthly.',
      sameAs: 'https://en.wikipedia.org/wiki/Software_as_a_service'
    },
    offeredBy: {
      '@id': '#routeOrg'
    },
    seller: {
      '@id': '#routeOrg'
    },
    url: 'https://getroute.com/pricing'
  }

  // const ProMonthlyOfferJSONLD = {
  //   '@type': 'Offer',
  //   name: 'Pro',
  //   priceSpecification: {
  //     '@type': 'UnitPriceSpecification',
  //     price: '399',
  //     priceCurrency: 'USD',
  //     referenceQuantity: {
  //       '@type': 'QuantitativeValue',
  //       priceType: 'Monthly',
  //       value: '1',
  //       unitCode: 'MON'
  //     }
  //   },
  //   acceptedPaymentMethod: 'http://purl.org/goodrelations/v1#ByInvoice',
  //   availability: 'OnlineOnly',
  //   businessFunction: 'http://purl.org/goodrelations/v1#ProvideService',
  //   category:
  //     'Software as a Service > Business Management Platform > Service Providers',
  //   eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
  //   itemOffered: {
  //     '@id': '#ProMonthly',
  //     '@type': 'Service',
  //     name: 'Pro Tier',
  //     description:
  //       'All Features including Recurring Services for Unlimited Users. Unlimited Users, Unlimited Walkthroughs, Proposals, and Inspections. Paid Monthly.',
  //     sameAs: 'https://en.wikipedia.org/wiki/Software_as_a_service'
  //   },
  //   offeredBy: {
  //   "@id": "#routeOrg",
  // },
  //   seller: {
  //   "@id": "#routeOrg",
  // },
  //   url: 'https://getroute.com/pricing'
  // }

  const StarterYearlyOfferJSONLD = {
    '@type': 'Offer',
    name: 'Starter',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '490',
      priceCurrency: 'USD',
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        priceType: 'Annual',
        value: '1',
        unitCode: 'ANN'
      }
    },
    acceptedPaymentMethod: 'http://purl.org/goodrelations/v1#ByInvoice',
    availability: 'OnlineOnly',
    businessFunction: 'http://purl.org/goodrelations/v1#ProvideService',
    category: 'Software as a Service > Business Management Platform > Service Providers',
    itemOffered: {
      '@id': '#StarterYearly',
      '@type': 'Service',
      name: 'Starter Tier',
      description:
        'All Sales Features for 10 Users. Unlimited Walkthroughs, and Inspections. Limit 5 Proposals. Paid Annually.',
      sameAs: 'https://en.wikipedia.org/wiki/Software_as_a_service'
    },
    offeredBy: {
      '@id': '#routeOrg'
    },
    seller: {
      '@id': '#routeOrg'
    },
    url: 'https://getroute.com/pricing'
  }

  const GrowthYearlyOfferJSONLD = {
    '@type': 'Offer',
    name: 'Growth',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '1290',
      priceCurrency: 'USD',
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        priceType: 'Annual',
        value: '1',
        unitCode: 'ANN'
      }
    },
    acceptedPaymentMethod: 'http://purl.org/goodrelations/v1#ByInvoice',
    availability: 'OnlineOnly',
    businessFunction: 'http://purl.org/goodrelations/v1#ProvideService',
    category: 'Software as a Service > Business Management Platform > Service Providers',
    itemOffered: {
      '@id': '#GrowthYearly',
      '@type': 'Service',
      name: 'Growth Tier',
      description:
        'All Sales Features for 10 Users. Unlimited Walkthroughs, Proposals, and Inspections. Paid Annually.',
      sameAs: 'https://en.wikipedia.org/wiki/Software_as_a_service'
    },
    offeredBy: {
      '@id': '#routeOrg'
    },
    seller: {
      '@id': '#routeOrg'
    },
    url: 'https://getroute.com/pricing'
  }

  // const ProYearlyOfferJSONLD = {
  //   '@type': 'Offer',
  //   name: 'Pro',
  //   priceSpecification: {
  //     '@type': 'UnitPriceSpecification',
  //     price: '3990',
  //     priceCurrency: 'USD',
  //     referenceQuantity: {
  //       '@type': 'QuantitativeValue',
  //       priceType: 'Annual',
  //       value: '1',
  //       unitCode: 'ANN'
  //     }
  //   },
  //   acceptedPaymentMethod: 'http://purl.org/goodrelations/v1#ByInvoice',
  //   availability: 'OnlineOnly',
  //   businessFunction: 'http://purl.org/goodrelations/v1#ProvideService',
  //   category:
  //     'Software as a Service > Business Management Platform > Service Providers',
  //   eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
  //   itemOffered: {
  //     '@id': '#ProYearly',
  //     '@type': 'Service',
  //     name: 'Pro Tier',
  //     description:
  //       'All Features including Recurring Services for Unlimited Users. Unlimited Users, Unlimited Walkthroughs, Proposals, and Inspections. Paid Annually.',
  //     sameAs: 'https://en.wikipedia.org/wiki/Software_as_a_service'
  //   },
  //   offeredBy: {
  //   "@id": "#routeOrg",
  // },
  //   seller: {
  //   "@id": "#routeOrg",
  // },
  //   url: 'https://getroute.com/pricing'
  // }

  const AggregateOfferJSONLD = {
    '@type': 'AggregateOffer',
    '@id': '#routeAggregateOffer',
    aggregateRating: {
      '@type': 'AggregateRating',
      itemReviewed: {
        '@id': '#routePlatform'
      },
      ratingValue: '5',
      ratingCount: '6'
    },
    highPrice: '1290.00',
    lowPrice: '49.00',
    offerCount: '4',
    offers: [
      // FreeOfferJSONLD,
      StarterMonthlyOfferJSONLD,
      GrowthMonthlyOfferJSONLD,
      // ProMonthlyOfferJSONLD,
      StarterYearlyOfferJSONLD,
      GrowthYearlyOfferJSONLD
      // ProYearlyOfferJSONLD
    ],
    priceCurrency: 'USD',
    priceSpecification: [
      {
        '@type': 'UnitPriceSpecification',
        price: '49',
        priceCurrency: 'USD',
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          priceType: 'Monthly',
          value: '1',
          unitCode: 'MON'
        }
      },
      {
        '@type': 'UnitPriceSpecification',
        price: '129',
        priceCurrency: 'USD',
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          priceType: 'Monthly',
          value: '1',
          unitCode: 'MON'
        }
      },
      {
        '@type': 'UnitPriceSpecification',
        price: '490',
        priceCurrency: 'USD',
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          priceType: 'Annual',
          value: '1',
          unitCode: 'ANN'
        }
      },
      {
        '@type': 'UnitPriceSpecification',
        price: '1290',
        priceCurrency: 'USD',
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          priceType: 'Annual',
          value: '1',
          unitCode: 'ANN'
        }
      }
    ]
  }

  const SoftwareApplicationJSONLD = {
    '@type': 'SoftwareApplication',
    '@id': '#routePlatform',
    // aggregateRating: {
    //   '@type': 'AggregateRating',
    //   ratingValue: '5',
    //   ratingCount: '6'
    // },
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Software as a Service',
    author: author || shortName,
    availableOnDevice: 'Android, ChromeOS, iOS, Linux, macOS, Windows 7, Windows 8, Windows 10',
    countriesSupported: 'USA, Canada, Mexico',
    downloadUrl: process.env.GATSBY_APP_URL,
    featureList: getFeatures(menuLinks),
    fileSize: `iOS: ${process.env.GATSBY_IOS_SIZE} --- Android: ${process.env.GATSBY_ANDROID_SIZE}`,
    installUrl: process.env.GATSBY_APP_URL,
    isAccessibleForFree: true,
    keywords,
    memoryRequirements: process.env.GATSBY_IOS_INSTALL,
    name: 'Route',
    // offers: {
    //   '@id': '#routeAggregateOffer'
    // },
    operatingSystem: 'Android, ChromeOS, iOS, Linux, macOS, Windows 7, Windows 8, Windows 10',
    permissions: 'camera, microphone, files, phone',
    // releaseNotes: 'https://getroute.com/releasenotes',
    // review: [UserReviewJSONLD],
    sameAs: 'https://en.wikipedia.org/wiki/Software_as_a_service',
    screenshot: process.env.GATSBY_IOS_INSTALL,
    // softwareHelp: ResourcesCreativeWorkJSONLD,
    softwareRequirements: 'Works best with Modern Browsers. Must enable JavaScript and must requires HTML5.',
    softwareVersion: process.env.GATSBY_CURRENT_APP_VERSION,
    sourceOrganization: {
      '@id': '#routeOrg'
    },
    storageRequirements: `iOS: ${process.env.GATSBY_IOS_SIZE} --- Android: ${process.env.GATSBY_ANDROID_SIZE}`,
    thumbnailUrl: `${siteUrl}/${defaultBanner}`,
    version: process.env.GATSBY_CURRENT_APP_VERSION
  }

  const WebApplicationJSONLD = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: defaultTitle,
    // aggregateRating: {
    //   '@type': 'AggregateRating',
    //   ratingValue: '5',
    //   ratingCount: '1'
    // },
    browserRequirements: 'JavaScript',
    applicationCategory: 'BusinessApplication',
    availableOnDevice: 'computer, laptop, tablet, smartphone',
    featureList: getFeatures(menuLinks),
    // REVIEW THESE
    // offers: {
    //   '@id': '#routeAggregateOffer'
    // },
    operatingSystem: 'Android, ChromeOS, iOS, Linux, macOS, Windows 7, Windows 8, Windows 10',
    permissions: 'camera, microphone, files, phone',
    // releaseNotes: 'https://getroute.com/releasenotes',
    screenshot: process.env.GATSBY_ANDROID_INSTALL,
    // softwareHelp: ResourcesCreativeWorkJSONLD,
    softwareRequirements: 'Works best with Modern Browsers. Must enable JavaScript and must requires HTML5.',
    softwareVersion: process.env.GATSBY_CURRENT_APP_VERSION,
    sourceOrganization: {
      '@id': '#routeOrg'
    }
  }

  const AndroidMobileApplicationJSONLD = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: defaultTitle,
    // aggregateRating: {
    //   '@type': 'AggregateRating',
    //   ratingValue: '5',
    //   ratingCount: '5'
    // },
    operatingSystem: 'ANDROID',
    applicationCategory: 'BusinessApplication',
    featureList: getFeatures(menuLinks),
    installUrl: process.env.GATSBY_ANDROID_INSTALL,
    // REVIEW THESE
    memoryRequirements: process.env.GATSBY_ANDROID_INSTALL,
    // offers: {
    //   '@id': '#routeAggregateOffer'
    // },
    permissions: 'camera, microphone, files, phone',
    processorRequirements: process.env.GATSBY_ANDROID_INSTALL,
    releaseNotes: process.env.GATSBY_ANDROID_INSTALL,
    screenshot: process.env.GATSBY_ANDROID_INSTALL,
    // softwareHelp: ResourcesCreativeWorkJSONLD,
    softwareRequirements: process.env.GATSBY_ANDROID_INSTALL,
    softwareVersion: process.env.GATSBY_CURRENT_APP_VERSION,
    storageRequirements: process.env.GATSBY_ANDROID_SIZE,
    sourceOrganization: {
      '@id': '#routeOrg'
    }
  }

  const iOSMobileApplicationJSONLD = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: defaultTitle,
    // aggregateRating: {
    //   '@type': 'AggregateRating',
    //   ratingValue: '5',
    //   ratingCount: '1'
    // },
    operatingSystem: 'iOS',
    applicationCategory: 'BusinessApplication',
    featureList: getFeatures(menuLinks),
    installUrl: process.env.GATSBY_IOS_INSTALL,
    // REVIEW THESE
    memoryRequirements: process.env.GATSBY_IOS_INSTALL,
    // offers: {
    //   '@id': '#routeAggregateOffer'
    // },
    permissions: 'camera, microphone, files, phone',
    processorRequirements: process.env.GATSBY_IOS_INSTALL,
    releaseNotes: process.env.GATSBY_IOS_INSTALL,
    screenshot: process.env.GATSBY_IOS_INSTALL,
    // softwareHelp: ResourcesCreativeWorkJSONLD,
    softwareRequirements: process.env.GATSBY_IOS_INSTALL,
    softwareVersion: process.env.GATSBY_CURRENT_APP_VERSION,
    storageRequirements: process.env.GATSBY_IOS_SIZE,
    sourceOrganization: {
      '@id': '#routeOrg'
    }
  }

  const pageJSONLD = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    breadcrumb,
    mainEntity: {
      '@id': '#routePlatform'
    }
  }

  // const getUpdatesJSONLD = {
  //   '@context': 'http://schema.org',
  //   '@type': 'SubscribeAction',
  //   agent: {
  //     '@id': '#routeOrg'
  //   },
  //   object: {
  //     '@type': 'Product',
  //     name: 'Route Nation Email Updates'
  //   },
  //   offers: {
  //     '@id': '#routeAggregateOffer'
  //   }
  // }

  const schemaOrgAppJSONLD = {
    '@context': 'http://schema.org',
    '@graph': [
      AggregateOfferJSONLD,
      AndroidMobileApplicationJSONLD,
      iOSMobileApplicationJSONLD,
      OrganizationJSONLD,
      WebApplicationJSONLD
    ]
  }

  const schemaOrgBusinessJSONLD = {
    '@context': 'http://schema.org',
    '@graph': [AggregateOfferJSONLD, BrandJSONLD, LocalBusinessJSONLD, OrganizationJSONLD, SoftwareApplicationJSONLD]
  }

  const schemaOrgWebSiteJSONLD = {
    '@context': 'http://schema.org',
    '@graph': [
      AggregateOfferJSONLD,
      // getUpdatesJSONLD,
      OrganizationJSONLD,
      SoftwareApplicationJSONLD,
      WebsiteJSONLD
    ]
  }

  const schemaOrgPageJSONLD = {
    '@context': 'http://schema.org',
    '@graph': [
      AggregateOfferJSONLD,
      // getUpdatesJSONLD,
      OrganizationJSONLD,
      pageJSONLD,
      SoftwareApplicationJSONLD
    ]
  }

  let schemaOrgJSONLD = [schemaOrgAppJSONLD, schemaOrgBusinessJSONLD, schemaOrgWebSiteJSONLD, schemaOrgPageJSONLD]

  // let schemaOrgJSONLD = [
  //   AggregateOfferJSONLD,
  //   BrandJSONLD,
  //   LocalBusinessJSONLD,
  //   OrganizationJSONLD,
  //   SoftwareApplicationJSONLD,
  //   AndroidMobileApplicationJSONLD,
  //   iOSMobileApplicationJSONLD,
  //   WebApplicationJSONLD,
  //   pageJSONLD,
  //   WebsiteJSONLD
  // ]

  /* Article Schema.org Starter */
  if (article) {
    let articleJSONLD = {
      '@context': 'http://schema.org',
      '@type': 'Article',
      '@id': seo.url,
      url: seo.url,
      about: section,
      accessMode: 'textual',
      audience: {
        alternateName: 'Service Providers',
        audienceType: 'Building Service Contractors',
        description:
          'Building Service Contractors performing services such as Day Porter Cleaning, Janitorial Cleaning, Post Construction Cleaning, Pressure Washing, Restoration and Emergency Services, Upholstery Cleaning, Window Washing, Astroturf Cleaning, Carpet Cleaning, Hardwood Cleaning, Concrete Polishing, Marble Polishing, Terrazzo Polishing, Tile and Grout, VCT Strip and Wax, Drywall, Electrical, Handyman Services, HVAC, Installation and Assembly, Landscaping, Moving Services,Painting, Pest Control, Plumbing, and Security.',
        disambiguatingDescription: 'Companies that provide Building Services',
        geographicArea: {
          '@type': 'Country',
          name: 'United States'
        },
        name: 'Building Service Contractors'
      },
      author: {
        '@type': 'Person',
        name: author || `Route Nation`
      },
      description,
      datePublished: new Date(date),
      dateModified: buildTime,
      // alternateName: defaultTitle || '',
      headline: title,
      // alternateHeadline: defaultTitle || '',
      // image: {
      //   '@type': 'ImageObject',
      //   url: seo.image
      // },
      inLanguage: {
        '@type': 'Language',
        name: 'English',
        alternateName: 'en'
      },
      isAccessibleForFree: true,
      isPartOf: siteUrl,
      keywords,
      // learningResourceType: [
      //   "how to",
      //   "learning activity",
      //   "subject matter expertise"
      // ],
      mainEntityOfPage: {
        '@id': '#routePlatform'
      },
      name: title,
      producer: {
        '@id': '#routeOrg'
      },
      provider: {
        '@id': '#routeOrg'
      },
      publisher: {
        '@id': '#routeOrg'
      },
      timeRequired: seo.timeToRead,
      // thumbnailUrl: seo.image,
      wordCount
    }

    if (seo.image) {
      articleJSONLD['thumbnailUrl'] = seo.image
      articleJSONLD['image'] = {
        '@type': 'ImageObject',
        url: seo.image
      }
    }

    if (backstory) {
      articleJSONLD['backstory'] = backstory
    }

    // if (body) {
    //   articleJSONLD['articleBody'] = body
    // }

    if (section) {
      articleJSONLD['articleSection'] = section
    }

    if (speakable) {
      articleJSONLD['speakable'] = {
        '@type': 'SpeakableSpecification',
        xpath: ['/html/head/title', "/html/head/meta[@name='description']/@content"]
        // cssSelector: ['headline', 'summary'],
        // ...speakable
      }
    }

    schemaOrgJSONLD.push({
      '@context': 'http://schema.org',
      '@graph': [AggregateOfferJSONLD, articleJSONLD, OrganizationJSONLD, SoftwareApplicationJSONLD]
    })
  }

  if (faq) {
    let faqJSONLD = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    }

    schemaOrgJSONLD.push(faqJSONLD)
  }

  if (howto) {
    let HowToJSONLD = {
      '@context': 'http://schema.org',
      '@type': 'HowTo',
      // image: {
      //   '@type': 'ImageObject',
      //   url: seo.image
      // },
      name: seo.title,
      description: seo.description,
      totalTime: seo.timeToRead,
      step: howto.steps.map((step, index) => ({
        '@type': 'HowToStep',
        name: step.name,
        text: step.text,
        image: `${step.url}${step.image}`,
        url: step.url
        /* EXAMPLE STEP 
        {
          '@type': 'HowToStep',
          name: 'Select Trivia Template',
          text:
            'Create a new project and select Templates | You have a choice to select between Trivia, Flashcard or Personality Quiz.',
          image: 'https://example.com/1x1/step2.jpg',
          url: 'https://example.com/tie#step2',
          video: {
            '@id': 'Clip2'
          }
        }
        */
      }))
    }

    if (seo.image) {
      HowToJSONLD['image'] = {
        '@type': 'ImageObject',
        url: seo.image
      }
    }

    if (howto.video) {
      HowToJSONLD['video'] = {
        '@type': 'VideoObject',
        name: howto.video.title,
        contentUrl: howto.video.url,
        description: howto.video.description,
        duration: `PT${howto.video.duration}S`,
        embedUrl: howto.video.src,
        interactionCount: howto.video.views,
        publisher: {
          '@id': '#routeOrg'
        },
        thumbnailUrl: howto.video.thumbnail,
        uploadDate: howto.video.date
        // hasPart:
        //   howto.video.clips &&
        //   howto.video.clips.map((clip, index) => ({
        //     '@type': 'Clip',
        //     '@id': `Clip${index}`,
        //     name: clip.name,
        //     startOffset: clip.startOffset,
        //     endOffset: clip.endOffset,
        //     url: clip.url
        //   }))
        /* EXAMPLE CLIP
          {
            '@type': 'Clip',
            '@id': 'Clip1',
            name: 'Open Actions on Google Console',
            startOffset: 29,
            endOffset: 36,
            url: 'http://www.example.com/example&t=29'
          }
          */
      }

      // HowToJSONLD.step.map((s, index) => ({
      //   ...s,
      //   video: {
      //     '@id': `Clip${index}`
      //   }
      // }))
    }

    // console.log(HowToJSONLD)
    schemaOrgJSONLD.push({
      '@context': 'http://schema.org',
      '@graph': [OrganizationJSONLD, HowToJSONLD]
    })
  }

  if (speakable) {
    pageJSONLD['speakable'] = {
      '@type': 'SpeakableSpecification',
      xpath: ['/html/head/title', "/html/head/meta[@name='description']/@content"]
      // cssSelector: ['headline', 'summary']
    }

    // pageJSONLD['speakable'] = {
    //   '@type': 'SpeakableSpecification',
    //   ...speakable
    // }
  }

  if (support) {
    let supportJSONLD = {
      '@type': 'CreativeWork',
      name: `https://resources.getroute.com/${pathname || ''}`,
      // aggregateRating: {
      //   '@type': 'AggregateRating',
      //   ratingValue: '5',
      //   ratingCount: '1'
      // },
      sameAs: seo.url
    }

    schemaOrgJSONLD.push(supportJSONLD)
  }

  if (video) {
    let videoJSONLD = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.title,
      description: video.description,
      thumbnailUrl: video.thumbnail,
      uploadDate: video.date,
      duration: `PT${video.duration}S`,
      publisher: {
        '@id': '#routeOrg'
      },
      contentUrl: video.url,
      embedUrl: video.src,
      interactionCount: video.views
    }

    // schemaOrgJSONLD.push({
    //   '@context': 'http://schema.org',
    //   '@graph': [OrganizationJSONLD, videoJSONLD]
    // })

    schemaOrgJSONLD.push(videoJSONLD)
  }

  return (
    <Helmet
      defer={false}
      // onChangeClientState={(newState, addedTags, removedTags) =>
      //   process.env.NODE_ENV === 'development' && console.log(buildTime, newState, addedTags, removedTags)
      // }
      // title={`${shortName} | ${seo.title}`}
      // titleTemplate={`${shortName} | ${seo.title}`}
    >
      {/* <html lang={siteLanguage} amp /> */}
      {/* process.env.NODE_ENV === 'production' && <base target="_blank" href={seo.url} /> */}
      {/* <link href="/manifest.json" rel="manifest" /> */}
      {/* <meta name="theme-color" content={themeColor} /> */}
      {/*  */}
      <html lang={siteLanguage} />
      <title itemProp="name" lang={siteLanguage}>
        {seo.metaTitle} | {shortName}
      </title>
      <meta charSet="utf-8" />
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {/* <meta name="referrer" content="origin-when-cross-origin" /> */}
      <link rel="home" href="https://getroute.com" />
      <meta name="author" content={shortName} />
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      {seo.image && <meta name="image" content={seo.image} />}
      {seo.image && <meta property="image:height" content={imageHeight} />}
      {seo.image && <meta property="image:width" content={imageWidth} />}
      <meta name="application-name" content={shortName} />
      <meta name="msapplication-TileColor" content={themeColor} />
      <meta name="apple-mobile-web-app-title" content={shortName} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      {/* {process.env.NODE_ENV === 'production' && <link rel="preconnect" href="https://api.layer.com" />} */}
      {/* <link rel="preconnect" href="https://conversations.app-us1.com" /> */}
      <link
        href="https://fonts.googleapis.com/css?family=Lato:300|Poppins:200,300,400,500,700&display=swap"
        rel="preload"
        as="style"
        onLoad={`this.rel='stylesheet'`}
      />
      {/* OpenGraph  */}
      <meta property="fb:app_id" content={process.env.GATSBY_FACEBOOK_APP_ID} />
      <meta property="og:site_name" content={shortName} />
      {seo.url && <meta property="og:url" content={seo.url} />}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      {seo.socialTitle && <meta property="og:title" content={`${seo.socialTitle} | ${shortName}`} />}
      {seo.socialDescription && <meta property="og:description" content={seo.socialDescription} />}
      {seo.image && <meta property="og:image" content={seo.image} />}
      {seo.image && <meta property="og:image:height" content={imageHeight} />}
      {seo.image && <meta property="og:image:width" content={imageWidth} />}
      {seo.image && <meta property="og:image:secure_url" content={seo.image} />}
      <meta property="og:locale" content="en_US" />
      {(article ? true : null) && <meta property="article:author" content={author} />}
      {(article ? true : null) && <meta property="article:publisher" content={shortName} />}
      {/* Twitter Card */}
      <meta name="twitter:site" content={social} />
      {seo.url && <meta name="twitter:url" content={seo.url} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={social} />
      {seo.title && <meta name="twitter:title" content={`${seo.socialTitle} | ${shortName}`} />}
      {seo.title && <meta name="twitter:text:title" content={`${seo.socialTitle} | ${shortName}`} />}
      {seo.description && <meta name="twitter:description" content={seo.socialDescription} />}
      {seo.image && <meta name="twitter:image" content={seo.image} />}
      {seo.image && <meta name="twitter:image:width" content={imageHeight} />}
      {seo.image && <meta name="twitter:image:height" content={imageWidth} />}
      <meta name="twitter:app:id:iphone" content={`1406939668`} />
      <meta name="twitter:app:id:ipad" content={`1406939668`} />
      <meta name="twitter:app:id:googleplay" content={`com.rozaroute.route`} />
      {/* Font Awesome */}
      <script type="text/javascript" defer src="https://kit.fontawesome.com/38c9e2f491.js" />
      {/* G2 Crowd */}
      <script id={`g2`} defer type={'text/javascript'}>
        {`(function(c,p,d,u,id,i){id=${JSON.stringify(
          userId
        )}||'';u='https://tracking.g2crowd.com/attribution_tracking/conversions/'+c+'.js?p='+encodeURI(p)+'&e='+id;i=document.createElement('script');i.type='application/javascript';i.async=true;i.src=u;d.getElementsByTagName('head')[0].appendChild(i);}("2553",document.location.href,document));`}
      </script>
      {/* Altassian Support Widget */}
      <script data-jsd-embedded data-key="5e5e8c32-68f8-407c-b1df-acceebeb09f1" data-base-url="https://jsd-widget.atlassian.com" src="https://jsd-widget.atlassian.com/assets/embed.js"></script>
      {/* Calendly */}
      <link href="https://assets.calendly.com/assets/external/widget.css" rel="stylesheet" />
      <script src="https://assets.calendly.com/assets/external/widget.js" type="text/javascript"></script>
      {/* Podcast Link */}
      <link type="application/rss+xml" rel="alternate" title="Cleaning and Cocktails" href="/podcasts.xml" />

      {schemaOrgJSONLD.map((schema, i) => (
        <script key={i + 1} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {children}
    </Helmet>
  )
}

export default Head

Head.propTypes = {
  article: PropTypes.bool,
  banner: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  backstory: PropTypes.string,
  body: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
  description: PropTypes.string,
  faq: PropTypes.arrayOf(PropTypes.object),
  howto: PropTypes.object,
  keywords: PropTypes.arrayOf(PropTypes.string),
  pathname: PropTypes.string,
  qa: PropTypes.bool,
  section: PropTypes.string,
  speakable: PropTypes.object,
  support: PropTypes.bool,
  timeToRead: PropTypes.number,
  title: PropTypes.string,
  video: PropTypes.object,
  wordCount: PropTypes.number
}

Head.defaultProps = {
  article: false,
  banner: {},
  date: Date.now(),
  description: '',
  keywords: defaultKeywords,
  pathname: '',
  qa: false,
  section: 'Building Services',
  support: false,
  timeToRead: 300,
  title: '',
  wordCount: 0
}

/* More Icons
  <meta
  content="/icons/mstile-70x70.png"
  name="msapplication-square70x70"
/>
<meta
  content="/icons/mstile-144x144.png"
  name="msapplication-square144x144"
/>
<meta
  content="/icons/mstile-150x150.png"
  name="msapplication-square150x150"
/>
<meta
  content="/icons/mstile-310x150.png"
  name="msapplication-wide310x150"
/>
<meta
  content="/icons/mstile-310x310.png"
  name="msapplication-square310x310"
/>

<link
  href="/icons/apple-touch-icon-57x57.png"
  rel="apple-touch-icon"
  sizes="57x57"
/>
<link
  href="/icons/apple-touch-icon-60x60.png"
  rel="apple-touch-icon"
  sizes="60x60"
/>
<link
  href="/icons/apple-touch-icon-72x72.png"
  rel="apple-touch-icon"
  sizes="72x72"
/>
<link
  href="/icons/apple-touch-icon-76x76.png"
  rel="apple-touch-icon"
  sizes="76x76"
/>
<link
  href="/icons/apple-touch-icon-114x114.png"
  rel="apple-touch-icon"
  sizes="114x114"
/>
<link
  href="/icons/apple-touch-icon-120x120.png"
  rel="apple-touch-icon"
  sizes="120x120"
/>
<link
  href="/icons/apple-touch-icon-144x144.png"
  rel="apple-touch-icon"
  sizes="144x144"
/>
<link
  href="/icons/apple-touch-icon-152x152.png"
  rel="apple-touch-icon"
  sizes="152x152"
/>
<link
  href="/icons/apple-touch-icon-167x167.png"
  rel="apple-touch-icon"
  sizes="167x167"
/>
<link
  href="/icons/apple-touch-icon-180x180.png"
  rel="icon"
  sizes="180x180"
  type="image/png"
/>
<link
  href="/icons/favicon-32x32.png"
  rel="icon"
  sizes="32x32"
  type="image/png"
/>
<link
  href="/icons/favicon-16x16.png"
  rel="icon"
  sizes="16x16"
  type="image/png"
/>
*/

/* QA Schema.org Starter
  if (qa) {
  schemaOrgJSONLD.push({
    '@context': 'https://schema.org',
    '@type': 'QAPage',
    mainEntity: {
      '@type': 'Question',
      name: '',
      text: '',
      answerCount: 0,
      upvoteCount: 0,
      dateCreated: buildTime,
      author: {
        '@type': 'Person',
        name: author
      },
      acceptedAnswer: {
        '@type': 'Answer',
        text: '',
        dateCreated: buildTime,
        upvoteCount: 0,
        url: `https://resources.getroute.com/qa/${pathname}#accepted`,
        author: {
          '@type': 'Person',
          name: 'Route'
        }
      },
      suggestedAnswer: [
        {
          '@type': 'Answer',
          text: '',
          dateCreated: buildTime,
          upvoteCount: 0,
          url: `https://resources.getroute.com/qa/${pathname}#suggested`,
          author: {
            '@type': 'Person',
            name: 'AnotherUser'
          }
        },
        {
          '@type': 'Answer',
          text: '',
          dateCreated: buildTime,
          upvoteCount: 0,
          url: `https://resources.getroute.com/qa/${pathname}#suggested`,
          author: {
            '@type': 'Person',
            name: 'AnotherUser'
          }
        }
      ]
    }
  })
}
*/

/* Example FAQs 

      [{
        "@type": "Question",
        "name": "What is the cancellation policy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most unopened items in new condition and returned within <strong>90 days</strong> will receive a refund or exchange. Some items have a modified return policy noted on the receipt or packing slip. Items that are opened or damaged or do not have a receipt may be denied a refund or exchange. Items purchased online or in-store may be returned to any store.<br /><p>Online purchases may be returned via a major parcel carrier. <a href='http://example.com/returns'> Click here </a> to initiate a return.</p>"
        }
      }, {
        "@type": "Question",
        "name": "How long does it take to process a refund?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We will reimburse you for returned items in the same way you paid for them. For example, any amounts deducted from a gift card will be credited back to a gift card. For returns by mail, once we receive your return, we will process it within 4–5 business days. It may take up to 7 days after we process the return to reflect in your account, depending on your financial institution's processing time."
        }
      }, {
        "@type": "Question",
        "name": "What is the policy for late/non-delivery of items ordered online?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our local teams work diligently to make sure that your order arrives on time, within our normaldelivery hours of 9AM to 8PM in the recipient's time zone. During  busy holiday periods like Christmas, Valentine's and Mother's Day, we may extend our delivery hours before 9AM and after 8PM to ensure that all gifts are delivered on time. If for any reason your gift does not arrive on time, our dedicated Customer Service agents will do everything they can to help successfully resolve your issue. <br/> <p><a href='https://example.com/orders/'>Click here</a> to complete the form with your order-related question(s).</p>"
        }
      }, {
        "@type": "Question",
        "name": "When will my credit card be charged?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We'll attempt to securely charge your credit card at the point of purchase online. If there's a problem, you'll be notified on the spot and prompted to use another card. Once we receive verification of sufficient funds, your payment will be completed and transferred securely to us. Your account will be charged in 24 to 48 hours."
        }
      }, {
        "@type": "Question",
        "name": "Will I be charged sales tax for online orders?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text":"Local and State sales tax will be collected if your recipient's mailing address is in: <ul><li>Arizona</li><li>California</li><li>Colorado</li></ul>"}
        }]

*/
